import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import EncounterReadOnlyHint from "@/components/encounter/EncounterReadOnlyHint";
import { ENC, PAT } from "@/constants/testIds";
import { ENCOUNTER_READ_ONLY_REASON, isEncounterReadOnly } from "@/lib/encounterApi";
import { encounterNumber, encounterStatusLabel } from "@/lib/patientChart";

export default function NewNoteDialog({
  open,
  onOpenChange,
  noteTypes = [],
  preferredTypeCode,
  encounters,
  encounterId,
  onEncounterIdChange,
  onCreate,
  locked = false,
}) {
  const preferred = noteTypes.find((type) => type.code === preferredTypeCode);
  const [noteTypeId, setNoteTypeId] = useState(preferred?.id || noteTypes[0]?.id || "");
  const [title, setTitle] = useState("");
  const showEncounter = Array.isArray(encounters);
  const selectedEncounter = showEncounter
    ? (encounters || []).find((enc) => enc.id === (encounterId || encounters[0]?.id)) || encounters[0]
    : null;
  const encounterLocked = locked || isEncounterReadOnly(selectedEncounter);

  useEffect(() => {
    if (!open) return;
    const next = noteTypes.find((type) => type.code === preferredTypeCode) || noteTypes[0];
    setNoteTypeId(next?.id || "");
    setTitle("");
  }, [open, preferredTypeCode, noteTypes]);

  const submit = async () => {
    const typeId = noteTypeId || noteTypes[0]?.id;
    if (!typeId) return;
    if (showEncounter && !encounterId && !encounters[0]?.id) return;
    if (encounterLocked) return;
    await onCreate?.({ noteTypeId: typeId, title, encounterId: encounterId || encounters?.[0]?.id });
    setTitle("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={ENC.screenNewNoteDialog} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Note</DialogTitle>
          <DialogDescription>
            Creates the Note identity only. Documentation starts when you write or add diagnoses.
            {showEncounter
              ? " The note is added to the selected encounter — a new encounter is not created."
              : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {showEncounter ? (
            <div className="space-y-1.5">
              <Label htmlFor="new-note-encounter">Encounter</Label>
              <select
                id="new-note-encounter"
                data-testid={PAT.chartNewNoteEncounter}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={encounterId || encounters[0]?.id || ""}
                onChange={(event) => onEncounterIdChange?.(event.target.value)}
              >
                {encounters.map((enc) => (
                  <option key={enc.id} value={enc.id}>
                    {encounterNumber(enc)}
                    {isEncounterReadOnly(enc) ? ` · ${encounterStatusLabel(enc.status)}` : ""}
                  </option>
                ))}
              </select>
              {encounterLocked ? (
                <p className="text-xs text-slate-500">{ENCOUNTER_READ_ONLY_REASON}</p>
              ) : null}
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="new-note-type">Note Type</Label>
            <select
              id="new-note-type"
              data-testid={ENC.screenNewNoteType}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={noteTypeId || noteTypes[0]?.id || ""}
              onChange={(event) => setNoteTypeId(event.target.value)}
            >
              {noteTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.displayName}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-note-title">Title (optional)</Label>
            <Input
              id="new-note-title"
              data-testid={ENC.screenNewNoteTitle}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="e.g. Lumbar Epidural Steroid Injection"
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <EncounterReadOnlyHint locked={encounterLocked}>
            <Button
              type="button"
              data-testid={ENC.screenNewNoteCreate}
              disabled={encounterLocked}
              onClick={submit}
            >
              Create Note
            </Button>
          </EncounterReadOnlyHint>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
